import React, { useCallback, useMemo } from 'react';
import { Body, Container, Head, HeadWrapper, RowNoDataWrapper, Th } from './styles';
import { useSortableTable } from './hooks/useSortableTable';
import TableRow from '#app/components/Table/TableRow';

const Table = React.memo((props) => {
  const { sorted, handleSort, sortItems, getWidth } = useSortableTable();

  const {
    fields,
    items = [],
    onRowClick,
    rowStyle,
    rowId,
    hover,
    noData,
    noDataText = 'Нет данных',
    header = true,
    hideHeader = false,
    headerStyle,
    id,
    containerStyle,
    style,
    bodyClassName,
  } = props;

  // Мемоизируем массив ключей полей
  const fieldsArray = useMemo(() => Object.keys(fields || {}), [fields]);

  // Оптимизированный поиск индекса
  const findSortedIndex = useCallback(
    (column) => {
      return sorted.findIndex((item) => item.column === column);
    },
    [sorted]
  );

  const getStyle = useCallback((item, key) => {
    if (item.hidden) {
      return {
        display: 'none',
        ...(typeof item.style === 'function' ? item.style(item, key) : item.style),
      };
    }

    return typeof item.style === 'function' ? item.style(item, key) : item.style || {};
  }, []);

  const handleRowClick = useCallback(
    (item, arrayKey) => {
      onRowClick?.(item, arrayKey);
    },
    [onRowClick]
  );

  const handleTdClick = useCallback((item, fields, key, arrayKey) => {
    fields[key]?.onClick?.(item, arrayKey);
  }, []);

  // Мемоизируем отсортированные элементы
  const sortedItems = useMemo(() => {
    return sortItems(items);
  }, [items, sortItems]);

  // Мемоизируем рендер строк
  const renderRows = useMemo(() => {
    if (!sortedItems?.length) return null;

    return sortedItems.map((item, arrayKey) => {
      const computedRowStyle = typeof rowStyle === 'function' ? rowStyle(item) : rowStyle;
      const computedRowId = typeof rowId === 'function' ? rowId(item) : rowId;
      const rowKey = item?.id ?? item?.attributes?.id ?? arrayKey;

      const rowProps = {
        item,
        fieldsArray,
        fields,
        arrayKey,
        onRowClick: handleRowClick,
        onTdClick: handleTdClick,
        getWidth,
        getStyle,
        ...(computedRowStyle && { rowStyle: computedRowStyle }),
        ...(computedRowId && { id: computedRowId }),
        ...(hover !== undefined && { hover }),
      };

      return <TableRow key={rowKey} {...rowProps} />;
    });
  }, [
    sortedItems,
    fieldsArray,
    fields,
    rowStyle,
    rowId,
    hover,
    handleRowClick,
    handleTdClick,
    getWidth,
    getStyle,
  ]);

  // Мемоизируем пустую строку
  const emptyRow = useMemo(() => {
    if (noData) {
      return noData;
    }

    if (noDataText !== false) {
      return (
        <RowNoDataWrapper>
          <div>
            <p>{noDataText}</p>
          </div>
        </RowNoDataWrapper>
      );
    }

    return null;
  }, [noData, noDataText]);

  // Мемоизируем заголовок
  const headerElement = useMemo(() => {
    if (header === false) return null;

    return (
      <Head style={{ display: hideHeader ? 'none' : 'block' }}>
        <HeadWrapper id={`table-header-${id}`} style={headerStyle}>
          {fieldsArray.map((fieldKey, index) => {
            const field = fields[fieldKey];
            const sortedIndex = findSortedIndex(index);
            const direction = sortedIndex !== -1 ? sorted[sortedIndex].direction : null;

            const name = typeof field.name === 'function' ? field.name() : field.name;

            return (
              <Th
                key={fieldKey}
                className={direction ? `sorted ${direction}` : ''}
                align={field.align || ''}
                onClick={() => handleSort(index, field.sort)}
                flexWidth={getWidth(field)}
                hideHeader={field.hideHeader || ''}
                style={getStyle(field, fieldKey)}
              >
                {name}
              </Th>
            );
          })}
        </HeadWrapper>
      </Head>
    );
  }, [
    header,
    hideHeader,
    headerStyle,
    id,
    fieldsArray,
    fields,
    findSortedIndex,
    sorted,
    handleSort,
    getWidth,
    getStyle,
  ]);

  return (
    <Container style={containerStyle}>
      {headerElement}
      <Body id={id} style={style} className={bodyClassName}>
        {items?.length > 0 ? renderRows : emptyRow}
      </Body>
    </Container>
  );
});

export default Table;
